import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';

// MUI
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';

// Custom
import AppPage from '@/ui/AppPage/AppPage';
import Permissions from '@/lib/permissions';
import TenantContext from '@/context/TenantContext/TenantContext';
import LoaderContext, { LoaderContextType } from '@/context/LoaderContext/LoaderContext';
import { ppService, PPRegistarStatistika, PPRegistarStatistikaIdlePlan } from '@/services/ppService';

const FILTER_ALL = 'all';
const IDLE_PAGE_SIZE = 10;

type IdleSortColumn = keyof Pick<
  PPRegistarStatistikaIdlePlan,
  'oznaka_plana' | 'naziv_plana' | 'vrsta_plana_naziv' | 'mbr_jls' | 'zadnja_promjena' | 'dana_bez_izmjene'
>;

type IdleColumn = {
  key: IdleSortColumn,
  ttoken: string,
  align?: 'right',
};

const idleColumns: IdleColumn[] = [
  { key: 'oznaka_plana', ttoken: 'pp:registar.oznaka_plana' },
  { key: 'naziv_plana', ttoken: 'pp:registar.naziv_plana' },
  { key: 'vrsta_plana_naziv', ttoken: 'pp:registar.vrsta_plana' },
  { key: 'mbr_jls', ttoken: 'pp:registar.mbr_jls' },
  { key: 'zadnja_promjena', ttoken: 'pp:registar.zadnja_promjena' },
  { key: 'dana_bez_izmjene', ttoken: 'pp:statistika.dana_bez_izmjene', align: 'right' },
];

const PPRegistarStatistikaPage: FC = () => {
  const { t } = useTranslation();
  const tenantContext = useContext(TenantContext);
  const loaderContext = useContext(LoaderContext) as LoaderContextType;

  const [statistika, setStatistika] = useState<PPRegistarStatistika | null>(null);
  const [vrstaFilter, setVrstaFilter] = useState<string>(FILTER_ALL);
  const [idleSortColumn, setIdleSortColumn] = useState<IdleSortColumn>('dana_bez_izmjene');
  const [idleSortDirection, setIdleSortDirection] = useState<'asc' | 'desc'>('desc');
  const [idlePage, setIdlePage] = useState(0);

  const novaColor = tenantContext?.tenantTheme?.primary_main || '#b7c800';
  const staraColor = tenantContext?.tenantTheme?.secondary_main || '#2a80b8';

  useEffect(() => {
    loaderContext.toggleLoading(true);
    ppService.getRegistarStatistika()
      .then((resp) => {
        if (resp.success && resp.data) {
          setStatistika(resp.data as PPRegistarStatistika);
        }
      })
      .finally(() => {
        loaderContext.toggleLoading(false);
      });
  }, []);

  const byVrsta = statistika?.by_vrsta || [];
  const idlePlans = statistika?.idle_plans || [];

  const sortedIdlePlans = useMemo(() => {
    const sorted = [...idlePlans].sort((a, b) => {
      const aValue = a[idleSortColumn];
      const bValue = b[idleSortColumn];
      if (aValue === bValue) return 0;
      return aValue > bValue ? 1 : -1;
    });
    return idleSortDirection === 'asc' ? sorted : sorted.reverse();
  }, [idlePlans, idleSortColumn, idleSortDirection]);

  const pagedIdlePlans = sortedIdlePlans.slice(
    idlePage * IDLE_PAGE_SIZE,
    idlePage * IDLE_PAGE_SIZE + IDLE_PAGE_SIZE
  );

  const handleIdleSort = (column: IdleSortColumn) => {
    setIdlePage(0);
    if (column === idleSortColumn) {
      setIdleSortDirection(idleSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setIdleSortColumn(column);
      setIdleSortDirection('asc');
    }
  };

  const pieLabels = [`${t('pp:statistika.nova_generacija')}`, `${t('pp:statistika.stara_generacija')}`];

  const pieSeries = useMemo(() => {
    if (vrstaFilter === FILTER_ALL) {
      return [
        byVrsta.reduce((sum, v) => sum + v.nova_count, 0),
        byVrsta.reduce((sum, v) => sum + v.stara_count, 0),
      ];
    }
    const selected = byVrsta.find((v) => v.vrsta_plana_id.toString() === vrstaFilter);
    return [selected?.nova_count || 0, selected?.stara_count || 0];
  }, [byVrsta, vrstaFilter]);

  const handleVrstaFilterChange = (evt: SelectChangeEvent) => {
    setVrstaFilter(evt.target.value);
  };

  return (
    <AppPage titlett="pp:page.statistika" needPermission={[Permissions.PPRegistarView]}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6">{t('pp:statistika.pie_title')}</Typography>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="pp-statistika-vrsta-filter-label">
                  {t('pp:statistika.vrsta_plana_filter')}
                </InputLabel>
                <Select
                  labelId="pp-statistika-vrsta-filter-label"
                  label={t('pp:statistika.vrsta_plana_filter')}
                  value={vrstaFilter}
                  onChange={handleVrstaFilterChange}
                >
                  <MenuItem value={FILTER_ALL}>{t('pp:statistika.vrsta_plana_filter_all')}</MenuItem>
                  {byVrsta.map((v) => (
                    <MenuItem key={v.vrsta_plana_id} value={v.vrsta_plana_id.toString()}>
                      {v.vrsta_plana_naziv}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box display="flex" justifyContent="center">
              <Chart
                key={`${pieLabels[0]}-${pieLabels[1]}`}
                type="pie"
                width={380}
                options={{
                  labels: pieLabels,
                  colors: [novaColor, staraColor],
                  legend: { position: 'bottom' },
                }}
                series={pieSeries}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" mb={2}>{t('pp:statistika.bar_title')}</Typography>
            <Chart
              type="bar"
              width="100%"
              height={380}
              options={{
                chart: { id: 'pp-statistika-bar', toolbar: { show: false } },
                xaxis: { categories: byVrsta.map((v) => v.vrsta_plana_oznaka || v.vrsta_plana_naziv) },
                colors: [novaColor, staraColor],
                legend: { position: 'bottom' },
              }}
              series={[
                { name: `${t('pp:statistika.nova_generacija')}`, data: byVrsta.map((v) => v.nova_count) },
                { name: `${t('pp:statistika.stara_generacija')}`, data: byVrsta.map((v) => v.stara_count) },
              ]}
            />
          </Paper>
        </Grid>

        <Grid size={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" mb={2}>{t('pp:statistika.idle_title')}</Typography>
            {idlePlans.length === 0 ? (
              <Typography color="text.secondary">{t('pp:statistika.idle_empty')}</Typography>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {idleColumns.map((column) => (
                          <TableCell key={column.key} align={column.align}>
                            <TableSortLabel
                              active={idleSortColumn === column.key}
                              direction={idleSortColumn === column.key ? idleSortDirection : 'asc'}
                              onClick={() => handleIdleSort(column.key)}
                            >
                              {t(column.ttoken)}
                            </TableSortLabel>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedIdlePlans.map((plan) => (
                        <TableRow key={plan.id}>
                          <TableCell>{plan.oznaka_plana}</TableCell>
                          <TableCell>{plan.naziv_plana}</TableCell>
                          <TableCell>{plan.vrsta_plana_naziv}</TableCell>
                          <TableCell>{plan.mbr_jls}</TableCell>
                          <TableCell>{new Date(plan.zadnja_promjena).toLocaleDateString()}</TableCell>
                          <TableCell align="right">{plan.dana_bez_izmjene}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={sortedIdlePlans.length}
                  page={idlePage}
                  onPageChange={(evt, page) => setIdlePage(page)}
                  rowsPerPage={IDLE_PAGE_SIZE}
                  rowsPerPageOptions={[IDLE_PAGE_SIZE]}
                />
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </AppPage>
  );
};

export default PPRegistarStatistikaPage;
